import { type FieldValues, type Path, type UseFormGetValues, type UseFormSetError } from 'react-hook-form';
import { useCallback } from 'react';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { useTranslations } from 'next-intl';

export interface BackendError {
  errors: Record<string, Array<string>> | string;
  message: string;
}

const flattenFormPaths = (obj: any, parentKey = '', paths: Array<string> = []) => {
  Object.entries(obj).forEach(([key, value]) => {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;
    paths.push(fullKey);

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenFormPaths(value, fullKey, paths);
    }
  });

  return paths;
};

export const useFormErrorHandler = <FormData extends FieldValues>(
  setError: UseFormSetError<FormData>,
  getValues: UseFormGetValues<FormData>,
  fallbackPath?: Path<FormData>,
) => {
  const { toast } = useToast();

  const tCommon = useTranslations('common');

  const handleError = useCallback(
    (errorObject: BackendError) => {
      if (errorObject.errors && typeof errorObject.errors === 'object') {
        const formPaths = flattenFormPaths(getValues());
        Object.entries(errorObject.errors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            const matchedPath = formPaths.includes(field) ? field : (fallbackPath ?? formPaths[0]);
            setError(matchedPath as Path<FormData>, {
              type: 'manual',
              message: messages[0],
            });
          }
        });
      } else {
        toast({
          variant: ToastVariant.DESTRUCTIVE,
          title: tCommon('error'),
          description: errorObject.message,
        });
      }
    },
    [getValues, fallbackPath, setError],
  );

  return { handleError };
};
